//app/components/cartSidebar.tsx
"use client";

import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useCart } from '@/app/contexts/cartContext';
import { X, Trash2, ShoppingCart, Star } from 'lucide-react';

// Define the shape of a service item, including the bundleId
interface Service {
  id: number;
  serviceName: string;
  bundleId: number | null;
  isDeleted?: boolean; // Added optional flag
}

// Define shape for Training if needed for validation
interface Training {
  id: number;
  trainingName: string; // or 'name' depending on your DB
  isDeleted?: boolean;
}

export default function CartSidebar() {
  const { isCartOpen, closeCart, cartItems, subtotal, removeFromCart, clearCart, updateQuantity, cartCount } = useCart();
  const [isMounted, setIsMounted] = useState(false);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const { data: session, status } = useSession();
  const router = useRouter();

  // Ref to ensure we don't validate in an infinite loop if cartItems changes
  const hasValidatedRef = useRef(false);

  const handleCheckout = () => {
    closeCart();

    if (status === "loading") return; // wait until session ready

    if (session) {
      router.push("/checkout");
    } else {
      router.push("/login?callbackUrl=/checkout");
    }
  };

  useEffect(() => {
    setIsMounted(true);

    const validateCart = () => {
      const itemsToRemove: string[] = [];

      cartItems.forEach(item => {
        // STRICT RULE: If the item is NOT a Bundle, mark it for removal.
        // This handles old Services and Trainings data.
        if (item.type !== 'Bundle') {
          itemsToRemove.push(item.name);
        }
      });

      // Execute Removal
      // if (itemsToRemove.length > 0) {
      //   // We use a timeout to ensure this doesn't conflict with the initial render cycle
      //   setTimeout(() => {
      //     itemsToRemove.forEach(name => removeFromCart(name));
      //     console.log("Auto-removed legacy items (only Bundles allowed):", itemsToRemove);
      //   }, 0);
      // }
    };

    // Run validation immediately on mount or when cart opens
    if (!hasValidatedRef.current || isCartOpen) {
      validateCart();
      hasValidatedRef.current = true;
    }

  }, [isCartOpen, cartItems, removeFromCart]);

  const serviceItemsCount = cartItems.filter(item => item.type === 'Service').length;

  const recommendationText = useMemo(() => {
    if (!allServices.length) return null;
    const cartServiceNames = cartItems.filter(item => item.type === 'Service').map(item => item.name);
    const totalServicesB1 = allServices.filter(s => s.bundleId === 1).length;
    const totalServicesB2 = allServices.filter(s => s.bundleId === 2).length;
    const totalServicesB3 = allServices.filter(s => s.bundleId === 3).length;
    const cartServicesB1Count = allServices.filter(s => s.bundleId === 1 && cartServiceNames.includes(s.serviceName)).length;
    const cartServicesB2Count = allServices.filter(s => s.bundleId === 2 && cartServiceNames.includes(s.serviceName)).length;
    const cartServicesB3Count = allServices.filter(s => s.bundleId === 3 && cartServiceNames.includes(s.serviceName)).length;
    const hasMoreThanHalfB1 = cartServicesB1Count > totalServicesB1 / 2;
    const hasMoreThanHalfB2 = cartServicesB2Count > totalServicesB2 / 2;
    const hasMoreThanHalfB3 = cartServicesB3Count > totalServicesB3 / 2;
    const notMoreThanHalfB2 = cartServicesB2Count <= totalServicesB2 / 2;
    const notMoreThanHalfB3 = cartServicesB3Count <= totalServicesB3 / 2;
    const hasEssentialBundleInCart = cartItems.some(item => item.type === 'Bundle' && item.name === 'Essential');
    const hasGrowthBundleInCart = cartItems.some(item => item.type === 'Bundle' && item.name === 'Growth');
    const isPremiumRecommended = (hasMoreThanHalfB1 && hasMoreThanHalfB2 && hasMoreThanHalfB3) || (hasMoreThanHalfB3 && serviceItemsCount > (totalServicesB1 + totalServicesB2 + totalServicesB3) / 2) || (hasGrowthBundleInCart && hasMoreThanHalfB3) || (hasEssentialBundleInCart && hasMoreThanHalfB2 && hasMoreThanHalfB3);
    if (isPremiumRecommended) return 'Save more with Premium Bundle!';
    const isGrowthRecommended = (hasMoreThanHalfB1 && hasMoreThanHalfB2 && notMoreThanHalfB3) || (hasMoreThanHalfB1 && hasMoreThanHalfB2 && notMoreThanHalfB3 && serviceItemsCount > (totalServicesB1 + totalServicesB2 + totalServicesB3) / 2) || (hasEssentialBundleInCart && hasMoreThanHalfB2 && notMoreThanHalfB3);
    if (isGrowthRecommended) return 'Save more with Growth Bundle!';
    const isEssentialRecommended = (hasMoreThanHalfB1 && notMoreThanHalfB2 && notMoreThanHalfB3 && serviceItemsCount <= (totalServicesB1 + totalServicesB2) / 2 && serviceItemsCount <= (totalServicesB1 + totalServicesB2 + totalServicesB3) / 2) || (hasMoreThanHalfB1 && notMoreThanHalfB2 && serviceItemsCount > (totalServicesB1 + totalServicesB2) / 2);
    if (isEssentialRecommended) return 'Save more with Essential Bundle!';
    return null;
  }, [cartItems, allServices, serviceItemsCount]);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeCart}
        className={`fixed inset-0 z-50 transition-opacity duration-300 ease-in-out bg-indigo-900/40 ${isCartOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center px-6 pt-10 pb-7">
            <h2 className="text-xl md:text-2xl text-slate-800 font-semibold">
              Cart View {isMounted && cartItems.length > 0 ? `(${cartCount})` : ''}
            </h2>
            <button onClick={closeCart} className="text-gray-400 hover:text-gray-800">
              <X size={20} className="md:w-6 md:h-6" />
            </button>
          </div>
          <div className="h-px bg-gray-200 mx-6"></div>

          {/* Clear All Cart */}
          {isMounted && cartItems.length > 0 && (
            <div className="mt-2 px-6 pb-2 text-right">
              <span onClick={clearCart} className="text-xs md:text-sm text-indigo-700 hover:underline cursor-pointer">
                Clear All Cart
              </span>
            </div>
          )}

          {/* --- SCROLLABLE CART ITEMS SECTION --- */}
          <div className="flex-grow px-6 pt-1 pb-2 overflow-y-auto">
            {isMounted && (
              <>
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="bg-slate-200 rounded-full w-28 h-28 md:w-32 md:h-32 flex items-center justify-center">
                      <ShoppingCart className='h-10 w-10 md:h-12 md:w-12 text-slate-400' />
                    </div>
                    <p className="mt-4 md:mt-6 text-base md:text-lg text-slate-500">Your cart is empty!</p>
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {cartItems.map((item, index) => (
                      <li key={index}>
                        <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm md:text-base font-semibold text-slate-800 mb-1">
                              {item.name}
                              {/* Display quantity for Training items */}
                              {(item.type === 'Training' || (item.type === 'Service' && item.isQuantifiable)) && ` (${item.quantity})`}
                            </p>

                            <div className="flex flex-col gap-0.5">
                              {/* --- ROW 1: Price + Tag --- */}
                              <div className="flex items-center gap-2">
                                <p className="text-xs md:text-sm text-slate-500">
                                  Price: RM{Number(item.newPrice).toFixed(2)}
                                </p>

                                {item.type === 'Bundle' && (
                                  <span className="bg-green-100 text-green-700 text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full">Bundle</span>
                                )}
                                {item.type === 'Service' && (
                                  <span className="bg-yellow-100 text-yellow-700 text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full">Service</span>
                                )}
                                {item.type === 'Training' && (
                                  <span className="bg-blue-100 text-blue-700 text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full">Training</span>
                                )}
                              </div>

                              {/* --- ROW 2: One-Time Fee --- */}
                              {item.oneTimeFee !== undefined && item.oneTimeFee > 0 && (
                                <p className="text-xs md:text-sm text-indigo-700">
                                  One-Time Fee: RM{Number(item.oneTimeFee).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* --- Quantity Adjuster and Remove Button --- */}
                          <div className="flex items-center gap-3">
                            {item.type === 'Training' || (item.type === 'Service' && item.isQuantifiable) ? (
                              <div className="flex items-center border bg-white border-gray-300 rounded-sm">
                                <button
                                  onClick={() => updateQuantity(item.name, item.quantity - 1)}
                                  className="px-2 text-slate-600 hover:text-indigo-800"
                                >-</button>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    const newQuantity = isNaN(value) ? 1 : value;
                                    updateQuantity(item.name, newQuantity);
                                  }}
                                  className="w-10 text-center text-xs md:text-sm font-semibold text-slate-700 border-l border-r border-gray-300 focus:outline-none"
                                  min="1"
                                />
                                <button
                                  onClick={() => updateQuantity(item.name, item.quantity + 1)}
                                  className="px-2 text-slate-600 hover:text-indigo-800"
                                >+</button>
                              </div>
                            ) : null}
                            <button
                              onClick={() => removeFromCart(item.name)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 md:h-5 md:w-5" />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* --- RECOMMENDATION CARD SECTION --- */}
          {isMounted && recommendationText && (
            <div className="px-6 py-3">
              <div className="animated-border rounded-lg p-4">
                <Link href="/bundles" passHref>
                  <div onClick={closeCart} className="flex items-center justify-center cursor-pointer">
                    <Star size={16} className="text-indigo-800 mr-2" />
                    <p className="text-sm font-semibold text-indigo-800 hover:underline">
                      {recommendationText}
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          )}

          <div className="h-px bg-gray-200 mx-6"></div>

          {/* --- FOOTER SECTION --- */}
          <div className="px-6 pt-5 pb-10">
            <div className="flex justify-between items-center mb-6">
              <span className="text-sm md:text-base text-slate-600">
                Subtotal:
              </span>
              <span className="font-semibold text-lg md:text-xl text-slate-800">
                RM{isMounted ? subtotal.toFixed(2) : '0.00'}
              </span>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleCheckout}
                className="w-full bg-gray-900 text-white font-semibold py-2.5 md:py-3 rounded-lg hover:bg-gray-800 transition-colors text-sm md:text-base"
              >
                Continue To Checkout
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animated-border {
          position: relative;
          border: none;
          z-index: 0;
        }
        .animated-border::after {
          content: '';
          position: absolute;
          inset: 0;
          background-color: #eef2ff;
          border-radius: inherit;
          z-index: -2; 
        }
        .animated-border::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          z-index: -1; 
          background:
            repeating-linear-gradient(90deg, #818cf8 0, #818cf8 6px, transparent 6px, transparent 12px),
            repeating-linear-gradient(180deg, #818cf8 0, #818cf8 6px, transparent 6px, transparent 12px),
            repeating-linear-gradient(-90deg, #818cf8 0, #818cf8 6px, transparent 6px, transparent 12px),
            repeating-linear-gradient(360deg, #818cf8 0, #818cf8 6px, transparent 6px, transparent 12px);
          background-size: 100% 2px, 2px 100%, 100% 2px, 2px 100%;
          background-position: 0 0, 100% 0, 0 100%, 0 0;
          background-repeat: no-repeat;
          animation: border-march 2s linear infinite;
        }
        @keyframes border-march {
          to {
            background-position: 12px 0, 100% 12px, -12px 100%, 0 -12px;
          }
        }
        /* Hide number input arrows */
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </>
  );
}